import React from "react";
import Link from "next/link";

import publicConfig from "config/public";
import useDomainCountryCode from "hooks/useDomainCountryCode";

import ExpandableContainer from "components/botanic/ExpandableContainer";

const { leaflyDomain, leaflyCaDomain } = publicConfig;

export type Locations = {
  country: string;
  countryLocations: {
    name: string;
    url?: string;
    locations: {
      name: string;
      url: string;
    }[];
  }[];
}[];

const modifyUrlForCountry = (
  domainCountryCode: string,
  url: string,
  country: string,
) => {
  if (domainCountryCode === "CA" && country === "United States") {
    return `${leaflyDomain}${url}`;
  } else if (domainCountryCode === "US" && country === "Canada") {
    return `${leaflyCaDomain}${url}`;
  } else {
    return url;
  }
};

const LocationLink: React.FC<{
  name: string;
  url: string;
}> = ({ name, url }) => (
  <Link href={url} className="block" data-testid={`${name.toLowerCase()}-link`}>
    {name}
  </Link>
);

const LocationAnchorTag: React.FC<{
  name: string;
  url: string;
}> = ({ name, url }) => (
  <a href={url} className="block" data-testid={`${name.toLowerCase()}-link`}>
    {name}
  </a>
);

const LocationsGrid: React.FC<{
  className?: string;
  locations?: Locations;
  useAnchorTags?: boolean;
}> = ({ locations, className, useAnchorTags = false }) => {
  const domainCountryCode = useDomainCountryCode();

  const LocationComponent = useAnchorTags ? LocationAnchorTag : LocationLink;

  return (
    <div className={className}>
      {locations?.map(({ country, countryLocations }) => (
        <div className="mb-xxl last:mb-none" key={country}>
          <h2 className="mb-md">{country}</h2>
          <div className="grid gap-lg grid-cols-2 md:grid-cols-4">
            {countryLocations.map(({ name, locations, url }) => (
              <div key={name}>
                <h3 className="mb-xs">
                  {url ? (
                    <LocationComponent
                      name={name}
                      url={modifyUrlForCountry(domainCountryCode, url, country)}
                    />
                  ) : (
                    name
                  )}
                </h3>
                {locations.length >= 6 ? (
                  <ExpandableContainer initialHeight="144px">
                    {locations.map(({ url, name }) => (
                      <LocationComponent
                        key={name}
                        name={name}
                        url={modifyUrlForCountry(
                          domainCountryCode,
                          url,
                          country,
                        )}
                      />
                    ))}
                  </ExpandableContainer>
                ) : (
                  <div>
                    {locations.map(({ url, name }) => (
                      <LocationComponent
                        key={name}
                        name={name}
                        url={modifyUrlForCountry(
                          domainCountryCode,
                          url,
                          country,
                        )}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LocationsGrid;
